import { DollarSign, Droplets, UserCheck } from 'lucide-react';
import { type MetricsResponse, Permission } from '@metron/shared';
import { MetricCard } from './MetricCard.tsx';
import { useMetricHistory } from '../hooks/useMetricHistory.ts';
import { usePermissions } from '../hooks/usePermissions.ts';
import { useWaterSavedMonthly } from '../hooks/useWaterSaved.ts';
import { cn } from '../lib/utils.ts';
import { DASHBOARD_METRICS, formatMonth, formatUsd } from '../pages/dashboardMetrics.ts';
import { sumByDay, sumOrBlank, waterSavedKpis } from '../pages/companyKpis.ts';

/** The Home-tab customers card, promoted into the KPI row rather than duplicated. */
export const KPI_CUSTOMERS_CARD_KEY = 'total-customers';

const formatGallons = (value: number): string =>
  `${
    value.toLocaleString('en-US', {
      notation: 'compact',
      maximumFractionDigits: 1,
    })
  } gal`;

/** A daily metric's trailing-30-day points, for summing into a combined trend. */
function useDailySeries(metricId: string, enabled: boolean, asOf?: string) {
  return useMetricHistory({ metricId, timeRange: '30d', enabled, asOf }).data?.dataPoints ?? [];
}

// '—' alone would look like "no data yet"; say the request failed instead.
const WATER_SAVED_LOAD_ERROR = "Couldn't load water-saved data. Try refreshing.";

interface CompanyKpisProps {
  metrics: MetricsResponse | null;
  isLoading: boolean;
  asOf?: string;
}

/**
 * Company KPI row (MET-3620): active users, customers, spend and gallons
 * saved, each with a trend, at the top of the Home tab.
 *
 * Water saved is monthly and computed once a month, so those two cards show
 * the newest computed month and ignore `asOf`.
 */
export function CompanyKpis({ metrics, isLoading, asOf }: CompanyKpisProps) {
  const { hasPermission } = usePermissions();
  const showSpend = hasPermission(Permission.VIEW_SPEND_METRICS);
  const classicUsers = useDailySeries('classic_unique_users_30d_external', !isLoading, asOf);
  const plusUsers = useDailySeries('plus_users_30d_external', !isLoading, asOf);
  const azureSpend = useDailySeries('azure_infra_spend_daily', showSpend && !isLoading, asOf);
  const awsSpend = useDailySeries('aws_infra_spend_daily', showSpend && !isLoading, asOf);

  const waterSaved = useWaterSavedMonthly();
  const ws = waterSavedKpis(waterSaved.data?.months ?? []);

  const customers = DASHBOARD_METRICS.find((m) => m.key === KPI_CUSTOMERS_CARD_KEY);

  return (
    <section aria-label='Company KPIs'>
      <div
        className={cn('grid gap-4 md:grid-cols-2', showSpend ? 'lg:grid-cols-5' : 'lg:grid-cols-4')}
      >
        <MetricCard
          title='Active Users (30d)'
          value={sumOrBlank(metrics?.classicUniqueUsers30dExternal, metrics?.plusUsers30dExternal)}
          breakdown={[
            { label: 'Classic', value: metrics?.classicUniqueUsers30dExternal },
            { label: 'Plus', value: metrics?.plusUsers30dExternal },
          ]}
          icon={<UserCheck className='h-4 w-4' />}
          isLoading={isLoading}
          description='External users active in WaterScope Classic or signed in to Plus in the trailing 30 days. Someone using both products counts twice.'
          timestamp={metrics?.computedAt?.classicUniqueUsers30dExternal}
          sparkValues={sumByDay(classicUsers, plusUsers)}
          asOf={asOf}
        />
        {customers && (
          <MetricCard
            title={customers.title}
            value={customers.getValue(metrics)}
            breakdown={customers.getBreakdown?.(metrics)}
            icon={<customers.icon className='h-4 w-4' />}
            isLoading={isLoading}
            description={customers.description}
            timestamp={customers.getTimestamp(metrics)}
            metricId={customers.metricId}
            granularity={customers.granularity}
            showHistory={customers.showHistory}
            asOf={asOf}
          />
        )}
        {showSpend && (
          <MetricCard
            title='Infra Spend (30d)'
            value={sumOrBlank(metrics?.azureSpend30d, metrics?.awsSpend30d)}
            breakdown={[
              { label: 'Azure', value: metrics?.azureSpend30d ?? undefined },
              { label: 'AWS', value: metrics?.awsSpend30d ?? undefined },
            ]}
            icon={<DollarSign className='h-4 w-4' />}
            isLoading={isLoading}
            description='Azure plus AWS (including Timescale) amortized infrastructure spend over the trailing 30 days. Trend is daily spend.'
            timestamp={metrics?.computedAt?.azureSpend30d}
            valueFormatter={formatUsd}
            lowerIsBetter
            sparkValues={sumByDay(azureSpend, awsSpend)}
            asOf={asOf}
          />
        )}
        <MetricCard
          title='Gallons Saved (Last Month)'
          value={ws.lastMonth?.value ?? '—'}
          icon={<Droplets className='h-4 w-4' />}
          isLoading={waterSaved.isLoading}
          description={waterSaved.isError
            ? WATER_SAVED_LOAD_ERROR
            : ws.lastMonth
            ? `Water saved from leaks fixed in ${
              formatMonth(ws.lastMonth.month)
            }. Trend is the last 12 months.`
            : 'Water saved from fixed leaks in the latest computed month.'}
          valueFormatter={formatGallons}
          sparkValues={ws.monthlySpark}
        />
        <MetricCard
          title='Gallons Saved (YTD)'
          value={ws.ytd?.value ?? '—'}
          icon={<Droplets className='h-4 w-4' />}
          isLoading={waterSaved.isLoading}
          description={waterSaved.isError
            ? WATER_SAVED_LOAD_ERROR
            : ws.ytd
            ? `Water saved from leaks fixed in ${ws.ytd.year}, through ${
              formatMonth(ws.ytd.throughMonth)
            }.`
            : 'Water saved from fixed leaks this year.'}
          valueFormatter={formatGallons}
          sparkValues={ws.ytdSpark}
          showDelta={false}
        />
      </div>
    </section>
  );
}
